import { getCurrentOperatorName } from '../../utils/currentOperator';
import { ABF_FACILITY_ENTRY } from '../../features/abf/routes';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import SecurityPicker, { PickedSecurity } from './shared/SecurityPicker';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { parseAmountShorthand } from '../../utils/amountUtils';
import {
  Activity,
  BadgeDollarSign,
  Banknote,
  Boxes,
  CheckCircle2,
  CircleDollarSign,
  Database,
  Layers,
  Percent,
  Search,
  Wallet,
} from 'lucide-react';

import { DateField } from '../shared/DateField';

const Workspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const WorkspaceBand = styled.section`
  border: 1px solid rgba(51, 65, 85, 0.74);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.9) 100%);
  border-radius: 14px;
  padding: 18px;
`;

const BandHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;

  @media (max-width: 860px) {
    flex-direction: column;
  }
`;

const BandTitleGroup = styled.div`
  min-width: 0;
`;

const BandEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 6px;
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.11em;
  text-transform: uppercase;
`;

const BandTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 18px;
  line-height: 1.25;
  letter-spacing: 0;
`;

const BandSubline = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
`;

const ScopePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const ScopePill = styled.div<{ $tone?: 'blue' | 'green' | 'amber' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tone = 'neutral' }) => {
    if ($tone === 'green') return 'rgba(52, 211, 153, 0.34)';
    if ($tone === 'amber') return 'rgba(245, 158, 11, 0.34)';
    if ($tone === 'blue') return 'rgba(56, 189, 248, 0.34)';
    return 'rgba(100, 116, 139, 0.34)';
  }};
  background: ${({ $tone = 'neutral' }) => {
    if ($tone === 'green') return 'rgba(20, 184, 166, 0.1)';
    if ($tone === 'amber') return 'rgba(245, 158, 11, 0.1)';
    if ($tone === 'blue') return 'rgba(56, 189, 248, 0.1)';
    return 'rgba(15, 23, 42, 0.64)';
  }};
  color: ${({ $tone = 'neutral' }) => {
    if ($tone === 'green') return '#5eead4';
    if ($tone === 'amber') return '#fbbf24';
    if ($tone === 'blue') return '#7dd3fc';
    return '#cbd5e1';
  }};
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const InstrumentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 0.8fr) minmax(0, 1.2fr);
  gap: 18px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SearchDock = styled.div`
  border: 1px solid rgba(51, 65, 85, 0.78);
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.24);
  padding: 16px;
  min-width: 0;

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const SearchTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-weight: 850;
  font-size: 13px;
`;

const SearchScope = styled.div`
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FacilityCard = styled.div`
  border: 1px solid rgba(45, 212, 191, 0.22);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(8, 47, 73, 0.36) 0%, rgba(15, 23, 42, 0.74) 52%, rgba(20, 83, 45, 0.16) 100%);
  padding: 16px;
  min-width: 0;
`;

const FacilityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

const FacilityIdentity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

const FacilityIcon = styled.div`
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.28);
  color: #7dd3fc;
`;

const FacilityName = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.2;
`;

const FacilityMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px 10px;
  margin-top: 7px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
`;

const FacilityMetaCode = styled.span`
  color: #93c5fd;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
  font-weight: 850;
`;

const AvailabilityPanel = styled.div`
  min-width: 260px;
  padding: 12px;
  border-radius: 11px;
  border: 1px solid rgba(71, 85, 105, 0.7);
  background: rgba(2, 6, 23, 0.28);
`;

const AvailabilityHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 850;
`;

const AvailabilityTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(51, 65, 85, 0.72);
  overflow: hidden;
`;

const AvailabilityFill = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => `${Math.min(100, Math.max(0, $pct))}%`};
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #22c55e 0%, #38bdf8 100%);
`;

const AvailabilityAmounts = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 9px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 750;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(128px, 1fr));
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Metric = styled.div`
  border: 1px solid rgba(51, 65, 85, 0.72);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.56);
  padding: 12px;
`;

const MetricLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const MetricValue = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.25;
`;

const PlaceholderPanel = styled.div`
  display: grid;
  place-items: center;
  min-height: 174px;
  border: 1px dashed rgba(100, 116, 139, 0.44);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.28);
  color: #64748b;
`;

const PlaceholderInner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 750;
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 0.42fr) minmax(280px, 0.58fr);
  gap: 16px;
  align-items: center;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const EventCard = styled.div`
  min-height: 76px;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid rgba(56, 189, 248, 0.32);
  background: rgba(56, 189, 248, 0.1);
`;

const EventGlyph = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(56, 189, 248, 0.12);
  color: #7dd3fc;
`;

const EventMain = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
  margin-bottom: 5px;
`;

const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PortfolioPanel = styled.div`
  padding: 14px;
  border-radius: 12px;
  border: 1px solid rgba(56, 189, 248, 0.28);
  background: rgba(56, 189, 248, 0.08);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const DetailPanel = styled.div`
  border: 1px solid rgba(51, 65, 85, 0.74);
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.2);
  padding: 16px;
`;

const DetailPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const DetailPanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 900;
`;

const SettlementTag = styled.div`
  color: #94a3b8;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const num = (value: any, fallback = 0) => {
  const n = value === '' || value == null ? fallback : parseAmountShorthand(String(value));
  return Number.isFinite(n) ? n : fallback;
};

const ABF_EVENT_PRESENTATION: Record<string, {
  title: string;
  action: string;
  cardTitle: string;
}> = {
  ACQUIRE: { title: 'Facility acquisition', action: 'ACQUIRE', cardTitle: 'Acquire ABF facility' },
  DRAWDOWN: { title: 'Facility drawdown', action: 'DRAWDOWN', cardTitle: 'Fund ABF draw' },
  PAYDOWN: { title: 'Facility paydown', action: 'PAYDOWN', cardTitle: 'Reduce funded balance' },
  REPAY: { title: 'Facility repayment', action: 'REPAY', cardTitle: 'Repay ABF draw' },
  PAYOFF: { title: 'Facility payoff', action: 'PAYOFF', cardTitle: 'Pay off ABF facility' },
  MTM: { title: 'Controlled valuation', action: 'MTM', cardTitle: 'Revalue ABF facility' },
  AMENDMENT: { title: 'Facility amendment', action: 'AMENDMENT', cardTitle: 'Amend ABF facility' },
  RATE_RESET: { title: 'Facility rate reset', action: 'RATE RESET', cardTitle: 'Reset ABF facility rate' },
  ACCRUAL: { title: 'Facility accrual', action: 'ACCRUAL', cardTitle: 'Accrue ABF financing' },
};

const fmtMoney = (value: any) =>
  num(value) > 0
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num(value))
    : '$0';

const fmtValue = (value: any, fallback = '—') =>
  value == null || value === '' ? fallback : String(value);

const fmtPct = (value: any) => {
  const n = num(value);
  return n > 0 ? `${n}%` : '—';
};

// Facility context; also feeds totalCommitment + drawnAmount into the form so
// buildTransactionData can post the right ACQUIRE consideration.
const AbfFacilityContext: React.FC<{
  securityId?: number | null;
  selectedName?: string;
  selectedCusip?: string;
  onLoaded?: (d: any) => void;
}> = ({ securityId, selectedName, selectedCusip, onLoaded }) => {
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!securityId) { setDetail(null); return; }
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`/api/credit/abf/securities/${securityId}/facility`);
        if (!cancelled) { setDetail(data || null); if (data) onLoaded?.(data); }
      } catch { if (!cancelled) setDetail(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityId]);

  if (!securityId || !detail) {
    return (
      <PlaceholderPanel>
        <PlaceholderInner>
          {loading ? <Activity size={17} /> : <Wallet size={17} />}
          <span>{loading ? 'Loading facility context' : 'Facility context pending'}</span>
        </PlaceholderInner>
      </PlaceholderPanel>
    );
  }

  const commitment = num(detail.totalCommitment);
  const drawn = num(detail.drawnAmount);
  // F-ABF-022: ABF capacity is constrained by the borrowing base, not by undrawn
  // commitment. `commitment - drawn` overstated it — on a $50M facility with a
  // $30.4M borrowing base and $20M drawn it showed $30M when only $10.4M was
  // actually drawable. The API already returns the correct figure.
  const availability = detail.excessAvailability != null
    ? Math.max(num(detail.excessAvailability), 0)
    : Math.max(commitment - drawn, 0);
  const drawnPct = commitment > 0 ? (drawn / commitment) * 100 : 0;
  const facilityName = detail.facilityName || detail.loanName || selectedName || detail.obligorName || 'ABF facility';

  return (
    <FacilityCard>
      <FacilityHeader>
        <FacilityIdentity>
          <FacilityIcon>
            <Boxes size={19} />
          </FacilityIcon>
          <div>
            <FacilityName>{facilityName}</FacilityName>
            <FacilityMeta>
              <FacilityMetaCode>{selectedCusip || detail.cusip || detail.facilityCode}</FacilityMetaCode>
              {detail.obligorName && <span>{detail.obligorName}</span>}
              {detail.collateralType && <span>{detail.collateralType}</span>}
              {detail.servicerName && <span>Servicer: {detail.servicerName}</span>}
            </FacilityMeta>
          </div>
        </FacilityIdentity>
        <AvailabilityPanel>
          <AvailabilityHeader>
            <span>Drawn</span>
            <span>{Number.isFinite(drawnPct) ? `${Math.round(drawnPct)}%` : '0%'}</span>
          </AvailabilityHeader>
          <AvailabilityTrack>
            <AvailabilityFill $pct={drawnPct} />
          </AvailabilityTrack>
          <AvailabilityAmounts>
            <span>{fmtMoney(drawn)}</span>
            <span>{fmtMoney(commitment)}</span>
          </AvailabilityAmounts>
        </AvailabilityPanel>
      </FacilityHeader>

      <MetricsGrid>
        <Metric>
          <MetricLabel>Commitment</MetricLabel>
          <MetricValue>{fmtMoney(commitment)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Availability</MetricLabel>
          <MetricValue style={{ color: '#7dd3fc' }}>{fmtMoney(availability)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Advance Rate</MetricLabel>
          <MetricValue>{fmtPct(detail.advanceRatePct)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Spread / Structure</MetricLabel>
          <MetricValue>
            {detail.spreadBps != null ? `${detail.spreadBps} bps` : fmtValue(detail.abfStructure)}
          </MetricValue>
        </Metric>
      </MetricsGrid>
    </FacilityCard>
  );
};

const ABFFacilityTicketScreen: React.FC = () => {
  const navigate = useNavigate();
  const initialFormData = {
    transactionType: 'BUY' as const,
    eventType: 'ACQUIRE',
    tradeAmount: '' as any, tradeQuantity: '' as any, tradePrincipal: '' as any,
    sourceRef: '',
    assetManager: '', portfolioManager: '',
    assetClass: 'ASSET_BASED_FINANCE', assetSubClass: 'ABF Facility',
    portfolio: '', tradeDate: getTodayDate(), settleDate: getTodayDate(),
    submittedBy: getCurrentOperatorName(), currency: 'USD', executedBy: '', notes: '',
    securityId: '' as any, cusip: '', security: '',
    // Pulled from the picked facility (reference data) for consideration math:
    facilityCommitment: '' as any, facilityDrawn: '' as any, facilityExcessAvailability: '' as any,
    markPct: '100',
  };

  // WS3: an acquisition split across funds. The rows live in the base ticket's Allocation Grid;
  // this mirror of them makes the ticket a BLOCK_PARENT with one commitment-only child per fund,
  // and the children's funds become the facility's holders when they post.
  const [abfAllocations, setAbfAllocations] = useState<Array<{ portfolioId: string; allocatedAmount: string }>>([]);
  const [abfUseAllocations, setAbfUseAllocations] = useState(false);
  const handleBaseAllocations = useCallback((rows: Array<{ portfolioId: string; allocatedAmount: string }>, on: boolean) => {
    setAbfAllocations(rows.map(r => ({ portfolioId: r.portfolioId || '', allocatedAmount: r.allocatedAmount || '' })));
    setAbfUseAllocations(on);
  }, []);
  const activeAllocations = (on?: boolean) => (on ?? abfUseAllocations)
    ? abfAllocations.filter(r => r.portfolioId && parseAmountShorthand(r.allocatedAmount) > 0) : [];
  const allocationError = (formData: any, rows: Array<{ portfolioId: string; allocatedAmount: string }>): string | null => {
    if (rows.length < 2) return 'Allocate across at least two funds, or switch the grid off and book one fund';
    const commitment = Math.max(num(formData.facilityCommitment) - num(formData.facilityDrawn), 0);
    const total = rows.reduce((s, r) => s + parseAmountShorthand(r.allocatedAmount), 0);
    if (Math.abs(total - commitment) > 0.005) return `Fund allocations total ${total.toLocaleString()} but the undrawn commitment is ${commitment.toLocaleString()}; they must reconcile exactly`;
    if (new Set(rows.map(r => r.portfolioId)).size !== rows.length) return 'A fund appears more than once; merge the rows';
    return null;
  };

  const validateForm = (formData: any, action: string, useAllocations?: boolean): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    const isCreate = action === 'draft' || action === 'submitForPreClear';
    const rows = activeAllocations(useAllocations);
    if (isCreate) {
      if (!formData.securityId) errors.securityId = 'Select an ABF facility (or define one in Reference Data)';
      if (rows.length === 0 && !formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
      if ((useAllocations ?? abfUseAllocations) && rows.length > 0) {
        const problem = allocationError(formData, rows);
        if (problem) errors.allocations = problem;
      }
      if (num(formData.markPct) <= 0) errors.markPct = 'Mark must be > 0';
      if (!formData.tradeDate) errors.tradeDate = 'Trade date is required';
    }
    if (action === 'execute' && !formData.executedBy?.trim()) errors.executedBy = 'Executed By is required';
    return errors;
  };

  const loadTransactionData = (transaction: any) => {
    return {
      ...initialFormData,
      transactionType: transaction.transactionType || 'BUY',
      eventType: transaction.eventType || 'ACQUIRE',
      tradeAmount: transaction.amount ?? '',
      tradeQuantity: transaction.quantity ?? '',
      tradePrincipal: transaction.principal ?? transaction.netAmount ?? '',
      sourceRef: transaction.sourceRef || '',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      securityId: transaction.securityId || '',
      cusip: transaction.cusip || '',
      security: transaction.securityName || transaction.cusip || '',
      markPct: transaction.price != null ? transaction.price.toString() : '100',
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
    };
  };

  const buildTransactionData = async (formData: any, dimensionData: any, useAllocations?: boolean): Promise<any> => {
    const rows = activeAllocations(useAllocations);
    if (rows.length > 0) {
      const problem = allocationError(formData, rows);
      if (problem) throw new Error(problem);
    }
    const portfolio = rows.length > 0
      ? dimensionData.portfolios.find((p: any) => String(p.id) === rows[0].portfolioId)
      : dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(rows.length > 0 ? 'The first allocated fund was not found' : `Portfolio "${formData.portfolio}" not found`);
    if (!formData.securityId) throw new Error('No ABF facility selected');
    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((a: any) => a.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((p: any) => p.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }
    const drawn = num(formData.facilityDrawn);
    const commitment = num(formData.facilityCommitment);
    const mark = num(formData.markPct, 100);
    // Commitment-only origination (drawn == 0) has no funded consideration.
    // ABFPostingSpi books no position or cash for it, so the trade record must
    // not carry phantom commitment-sized economics either — pre-clear compliance
    // evaluates these fields.
    const consideration = drawn * mark / 100;
    return {
      transactionType: 'BUY',
      securityId: Number(formData.securityId),
      portfolioId: portfolio.id,
      assetManagerId, portfolioManagerId,
      quantity: drawn,
      price: mark,
      amount: drawn,
      // Contractual exposure of the undrawn portion — required evidence for a
      // commitment-only (amount = 0) origination, mirroring PRIVATE_CREDIT.
      unfundedCommitment: Math.max(commitment - drawn, 0),
      principal: consideration,
      netAmount: consideration,
      tradeDate: formData.tradeDate,
      settleDate: formData.tradeDate,
      notes: formData.notes || `ABF facility acquisition: ${formData.security || formData.cusip}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'ABF',
      eventType: 'ACQUIRE',
      sourceRef: rows.length > 0 ? undefined : `abf:security-${formData.securityId}:acquire`,
      currency: formData.currency || 'USD',
      cusip: formData.cusip,
      // WS3: with the grid active the backend builds a BLOCK_PARENT plus one commitment-only
      // ALLOCATION_CHILD per fund (the allocation is each fund's commitment share).
      allocations: rows.length > 0
        ? rows.map(r => ({ portfolioId: Number(r.portfolioId), allocatedAmount: parseAmountShorthand(r.allocatedAmount) }))
        : undefined,
    };
  };

  return (
    <BaseTicketScreen
      title="ABF Facility Trade Entry"
      assetClass="ASSET_BASED_FINANCE"
      assetSubClass="ABF Facility"
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
      hidePortfolioField={true}
      onAllocationsChange={handleBaseAllocations}
      aiSuggestionsLabel="Get ABF Facility Ideas"
      aiSuggestionsLoadingLabel="Getting ideas..."
      aiSuggestionsAssetClass="ASSET_BASED_FINANCE"
      aiSuggestionsSubMessage="Analyzing ABF exposure, borrowing-base capacity, and facility-level opportunities"
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange }) => {
        const pickerLocked = isFieldDisabled('securityId') || (!!transactionStatus && transactionStatus !== 'DRAFT');
        const selected: PickedSecurity | null = formData.securityId
          ? { id: Number(formData.securityId), cusip: formData.cusip, securityName: formData.security } : null;
        const drawn = num(formData.facilityDrawn);
        const commitment = num(formData.facilityCommitment);
        // F-ABF-022: borrowing-base-constrained capacity, not undrawn commitment.
        const availability = formData.facilityExcessAvailability !== '' && formData.facilityExcessAvailability != null
          ? Math.max(num(formData.facilityExcessAvailability), 0)
          : Math.max(commitment - drawn, 0);
        const mark = num(formData.markPct, 100);
        const eventType = String(formData.eventType || 'ACQUIRE').toUpperCase();
        const eventTypeLabel = eventType.replace(/_/g, ' ');
        const eventPresentation = ABF_EVENT_PRESENTATION[eventType] || {
          title: 'Facility lifecycle event',
          action: eventTypeLabel,
          cardTitle: `Process ${eventTypeLabel.toLowerCase()}`,
        };
        const transactionSide = String(formData.transactionType || 'BUY').toUpperCase();
        const isPersistedTicket = Boolean(transactionStatus);
        const tradeAmount = num(formData.tradeAmount);
        const tradeQuantity = num(formData.tradeQuantity);
        const tradePrincipal = formData.tradePrincipal !== '' && formData.tradePrincipal != null
          ? num(formData.tradePrincipal)
          : tradeAmount * mark / 100;
        const consideration = isPersistedTicket ? tradePrincipal : drawn * mark / 100;
        const instrumentTitle = formData.security || 'Awaiting facility';
        const instrumentCode = formData.cusip || 'No CUSIP';

        return (
          <Workspace>
            <WorkspaceBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow>
                    <Search size={14} />
                    Asset-based finance facility
                  </BandEyebrow>
                  <BandTitle>{instrumentTitle}</BandTitle>
                  <BandSubline>{selected ? instrumentCode : 'Receivables, specialty finance, and borrowing-base facilities'}</BandSubline>
                </BandTitleGroup>
                <ScopePills>
                  <ScopePill $tone="blue"><BadgeDollarSign size={13} /> {eventPresentation.action}</ScopePill>
                  <ScopePill $tone="green">{transactionSide}</ScopePill>
                </ScopePills>
              </BandHeader>

              <InstrumentGrid>
                <SearchDock>
                  <SearchHeader>
                    <SearchTitle>
                      <Layers size={15} />
                      Instrument
                    </SearchTitle>
                    <SearchScope>ABF facility</SearchScope>
                  </SearchHeader>
                  <SecurityPicker
                    assetClass="ASSET_BASED_FINANCE"
                    label="ABF Facility (CUSIP / Obligor)*"
                    placeholder="Search by facility, obligor, or CUSIP..."
                    selected={selected}
                    disabled={pickerLocked}
                    error={errors.securityId}
                    notFoundLabel="Define new ABF facility"
                    onDefineNew={() => navigate(ABF_FACILITY_ENTRY)}
                    onSelect={(sec) => setFormData((prev: any) => ({
                      ...prev,
                      securityId: sec.id,
                      cusip: sec.cusip || '',
                      security: sec.securityName || sec.cusip || '',
                      currency: sec.currency || prev.currency,
                    }))}
                    onClear={() => setFormData((prev: any) => ({
                      ...prev,
                      securityId: '',
                      cusip: '',
                      security: '',
                      facilityCommitment: '',
                      facilityDrawn: '',
                      facilityExcessAvailability: '',
                    }))}
                  />
                </SearchDock>

                <AbfFacilityContext
                  securityId={formData.securityId ? Number(formData.securityId) : null}
                  selectedName={formData.security}
                  selectedCusip={formData.cusip}
                  onLoaded={(d) => setFormData((prev: any) => ({
                    ...prev,
                    facilityCommitment: d.totalCommitment != null ? d.totalCommitment : prev.facilityCommitment,
                    facilityDrawn: d.drawnAmount != null ? d.drawnAmount : prev.facilityDrawn,
                    facilityExcessAvailability: d.excessAvailability != null ? d.excessAvailability : prev.facilityExcessAvailability,
                  }))}
                />
              </InstrumentGrid>
            </WorkspaceBand>

            <WorkspaceBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow>
                    <BadgeDollarSign size={14} />
                    Lifecycle event
                  </BandEyebrow>
                  <BandTitle>{eventPresentation.title}</BandTitle>
                  <BandSubline>{eventPresentation.action} / {transactionSide} / {formData.settleDate || formData.tradeDate}</BandSubline>
                </BandTitleGroup>
                <ScopePills>
                  <ScopePill $tone="neutral"><CheckCircle2 size={13} /> IBOR trade</ScopePill>
                  <ScopePill $tone="blue">{eventPresentation.action}</ScopePill>
                </ScopePills>
              </BandHeader>

              <EventGrid>
                <EventCard>
                  <EventGlyph>
                    <Database size={17} />
                  </EventGlyph>
                  <div>
                    <EventMain>{eventPresentation.cardTitle}</EventMain>
                    <EventMeta>
                      <span>{eventPresentation.action}</span>
                      <span>{transactionSide}</span>
                      <span>ABF</span>
                    </EventMeta>
                  </div>
                </EventCard>

                <PortfolioPanel>
                  <FormGroup style={{ margin: 0 }}>
                    <Label>Portfolio*</Label>
                    <Select
                      value={formData.portfolio}
                      onChange={(e) => handleInputChange('portfolio', e.target.value)}
                      className={errors.portfolio ? 'error' : ''}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">Select Portfolio</option>
                      {dimensionData.portfolios.map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>
                      ))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                </PortfolioPanel>
              </EventGrid>
            </WorkspaceBand>

            <WorkspaceBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow>
                    <Banknote size={14} />
                    Ticket economics
                  </BandEyebrow>
                  <BandTitle>Facility terms</BandTitle>
                  <BandSubline>{instrumentCode}</BandSubline>
                </BandTitleGroup>
                <ScopePills>
                  <ScopePill $tone="neutral"><Percent size={13} /> Mark {mark || 100}%</ScopePill>
                  <ScopePill $tone="neutral">{formData.currency}</ScopePill>
                </ScopePills>
              </BandHeader>

              <DetailGrid>
                <DetailPanel>
                  <DetailPanelHeader>
                    <DetailPanelTitle>
                      <CircleDollarSign size={16} />
                      Economics
                    </DetailPanelTitle>
                    <SettlementTag>{eventPresentation.action}</SettlementTag>
                  </DetailPanelHeader>

                  <MetricsGrid style={{ marginBottom: 14 }}>
                    <Metric>
                      <MetricLabel>{isPersistedTicket ? 'Event amount' : 'Drawn'}</MetricLabel>
                      <MetricValue>{fmtMoney(isPersistedTicket ? tradeAmount : drawn)}</MetricValue>
                    </Metric>
                    <Metric>
                      <MetricLabel>{isPersistedTicket ? 'Quantity' : 'Commitment'}</MetricLabel>
                      <MetricValue>{fmtMoney(isPersistedTicket ? tradeQuantity : commitment)}</MetricValue>
                    </Metric>
                    <Metric>
                      <MetricLabel>{isPersistedTicket ? 'Price' : 'Availability'}</MetricLabel>
                      <MetricValue style={{ color: '#7dd3fc' }}>
                        {isPersistedTicket ? `${mark}%` : fmtMoney(availability)}
                      </MetricValue>
                    </Metric>
                    <Metric>
                      <MetricLabel>Consideration</MetricLabel>
                      <MetricValue>{fmtMoney(consideration)}</MetricValue>
                    </Metric>
                  </MetricsGrid>

                  <FormRow>
                    <FormGroup>
                      <Label>Mark (% of par)*</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.markPct}
                        onChange={(e) => handleInputChange('markPct', e.target.value)}
                        className={errors.markPct ? 'error' : ''}
                        disabled={isFieldDisabled('markPct')}
                      />
                      {errors.markPct && <FieldError>{errors.markPct}</FieldError>}
                    </FormGroup>
                  </FormRow>
                </DetailPanel>

                <DetailPanel>
                  <DetailPanelHeader>
                    <DetailPanelTitle>
                      <Activity size={16} />
                      Desk details
                    </DetailPanelTitle>
                    <SettlementTag>{formData.tradeDate}</SettlementTag>
                  </DetailPanelHeader>

                  <FormRow>
                    <FormGroup>
                      <Label>Trade Date*</Label>
                      {/* Settle is anchored to trade (buildTransactionData sends settle = trade); keep the shown settle in step. */}
                      <DateField
                        value={formData.tradeDate ?? ''}
                        className={errors.tradeDate ? 'error' : ''}
                        disabled={isFieldDisabled('tradeDate')}
                        calendars={calendarsFor('ABF', formData.currency)}
                        onChange={iso => { handleInputChange('tradeDate', iso); handleInputChange('settleDate', iso); }}
                      />
                      {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
                    </FormGroup>
                    <FormGroup>
                      <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                      <Select
                        value={formData.executedBy}
                        onChange={(e) => handleInputChange('executedBy', e.target.value)}
                        disabled={isFieldDisabled('executedBy')}
                        className={errors.executedBy ? 'error' : ''}
                      >
                        <option value="">Select</option>
                        {dimensionData.users.map((u: any) => (
                          <option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>
                        ))}
                      </Select>
                      {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Asset Manager</Label>
                      <Select
                        value={formData.assetManager}
                        onChange={(e) => handleInputChange('assetManager', e.target.value)}
                        disabled={isFieldDisabled('assetManager')}
                      >
                        <option value="">Select</option>
                        {dimensionData.assetManagers.map((am: any) => (
                          <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
                        ))}
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label>Portfolio Manager</Label>
                      <Select
                        value={formData.portfolioManager}
                        onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                        disabled={isFieldDisabled('portfolioManager')}
                      >
                        <option value="">Select</option>
                        {dimensionData.portfolioManagers.map((pm: any) => (
                          <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
                        ))}
                      </Select>
                    </FormGroup>
                  </FormRow>
                </DetailPanel>
              </DetailGrid>
            </WorkspaceBand>
          </Workspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default ABFFacilityTicketScreen;
